import React from 'react';
import { Table, Tag, Input, Select, Progress, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { ChainListItem, ChainFilters } from '../../../services/devApi';

const { Text } = Typography;

const statusColorMap: Record<string, string> = {
  LIVE_VERIFIED: 'green',
  INTEGRATION_VERIFIED: 'blue',
  PARTIALLY_CONNECTED: 'orange',
  MODULES_EXIST_BUT_CHAIN_BROKEN: 'red',
  NOT_STARTED: 'default',
};

const evidenceColorMap: Record<string, string> = {
  LIVE_VERIFIED: 'green',
  INTEGRATION_VERIFIED: 'cyan',
  E2E_VERIFIED: 'blue',
  UNIT_VERIFIED: 'geekblue',
  MODULE_EXISTS: 'orange',
  ROUTE_REGISTERED: 'gold',
  UNKNOWN: 'default',
};

const domainColorMap: Record<string, string> = {
  Sales: 'blue',
  Purchase: 'green',
  Inventory: 'cyan',
  'R&D': 'purple',
  QC: 'magenta',
  HR: 'volcano',
  Finance: 'gold',
  Production: 'orange',
  System: 'geekblue',
};

const STATUS_OPTIONS = [
  { value: '', label: '全部狀態' },
  { value: 'LIVE_VERIFIED', label: 'LIVE VERIFIED' },
  { value: 'INTEGRATION_VERIFIED', label: 'INTEGRATION VERIFIED' },
  { value: 'PARTIALLY_CONNECTED', label: 'PARTIALLY CONNECTED' },
  { value: 'MODULES_EXIST_BUT_CHAIN_BROKEN', label: 'MODULES EXIST (BROKEN)' },
  { value: 'NOT_STARTED', label: 'NOT STARTED' },
];

const DOMAIN_OPTIONS = [
  { value: '', label: '全部領域' },
  { value: 'Sales', label: 'Sales' },
  { value: 'Purchase', label: 'Purchase' },
  { value: 'Inventory', label: 'Inventory' },
  { value: 'R&D', label: 'R&D' },
  { value: 'QC', label: 'QC' },
  { value: 'HR', label: 'HR' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Production', label: 'Production' },
  { value: 'System', label: 'System' },
];

interface Props {
  chains: ChainListItem[];
  total: number;
  loading: boolean;
  filters: ChainFilters;
  onSearch: (value: string) => void;
  onDomainFilter: (value: string) => void;
  onStatusFilter: (value: string) => void;
  onPageChange: (page: number, size: number) => void;
  onRowClick: (chainId: string) => void;
}

const ChainTable: React.FC<Props> = ({
  chains,
  total,
  loading,
  filters,
  onSearch,
  onDomainFilter,
  onStatusFilter,
  onPageChange,
  onRowClick,
}) => {
  const columns: ColumnsType<ChainListItem> = [
    {
      title: 'Chain ID',
      dataIndex: 'chainId',
      key: 'chainId',
      width: 180,
      render: (id: string) => <span className="font-mono font-bold">{id}</span>,
    },
    {
      title: '鏈條名稱',
      dataIndex: 'chainName',
      key: 'chainName',
      width: 200,
      ellipsis: true,
    },
    {
      title: '領域',
      dataIndex: 'businessDomain',
      key: 'businessDomain',
      width: 100,
      render: (domain: string) => (
        <Tag color={domainColorMap[domain] || 'default'}>{domain}</Tag>
      ),
    },
    {
      title: '狀態',
      dataIndex: 'currentStatus',
      key: 'currentStatus',
      width: 160,
      render: (status: string) => (
        <Tag color={statusColorMap[status] || 'default'}>
          {status.replace(/_/g, ' ')}
        </Tag>
      ),
    },
    {
      title: '證據等級',
      dataIndex: 'evidenceLevel',
      key: 'evidenceLevel',
      width: 140,
      render: (level: string) => (
        <Tag color={evidenceColorMap[level] || 'default'}>
          {level.replace(/_/g, ' ')}
        </Tag>
      ),
    },
    {
      title: '起始表單',
      dataIndex: 'startFormId',
      key: 'startFormId',
      width: 100,
      render: (formId: string) => <span className="font-mono">{formId}</span>,
    },
    {
      title: '核准',
      dataIndex: 'approvalCount',
      key: 'approvalCount',
      width: 70,
      align: 'center',
      render: (count: number) => count > 0
        ? <Tag color="volcano">{count}</Tag>
        : <span className="text-gray-400">--</span>,
    },
    {
      title: '節點數',
      dataIndex: 'nodeCount',
      key: 'nodeCount',
      width: 80,
      align: 'center',
    },
    {
      title: '進度',
      key: 'progress',
      width: 150,
      render: (_: unknown, record: ChainListItem) => {
        const percent = record.nodeCount > 0
          ? Math.round((record.readyCount / record.nodeCount) * 100)
          : 0;
        return (
          <div>
            <Progress
              percent={percent}
              size="small"
              strokeColor={record.blockedCount > 0 ? '#ff4d4f' : undefined}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.readyCount}/{record.nodeCount}
              {record.inProgressCount > 0 && (
                <span style={{ color: '#1677ff', marginLeft: 4 }}>
                  ({record.inProgressCount} in progress)
                </span>
              )}
              {record.blockedCount > 0 && (
                <span style={{ color: '#ff4d4f', marginLeft: 4 }}>
                  ({record.blockedCount} blocked)
                </span>
              )}
            </Text>
          </div>
        );
      },
    },
    {
      title: '更新時間',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 110,
      render: (val: string) => {
        if (!val) return '--';
        const d = new Date(val);
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${mm}-${dd} ${hh}:${min}`;
      },
    },
  ];

  return (
    <div>
      <div className="flex items-center gap-4 mb-4">
        <Input.Search
          placeholder="搜尋 chainId / 名稱 / formId..."
          allowClear
          style={{ width: 300 }}
          prefix={<SearchOutlined />}
          defaultValue={filters.search}
          onSearch={onSearch}
        />
        <Select
          placeholder="篩選領域"
          allowClear
          style={{ width: 150 }}
          value={filters.domain || undefined}
          onChange={(val) => onDomainFilter(val || '')}
          options={DOMAIN_OPTIONS}
        />
        <Select
          placeholder="篩選狀態"
          allowClear
          style={{ width: 200 }}
          value={filters.status || undefined}
          onChange={(val) => onStatusFilter(val || '')}
          options={STATUS_OPTIONS}
        />
        <Text type="secondary">共 {total} 條鏈</Text>
      </div>
      <Table<ChainListItem>
        columns={columns}
        dataSource={chains}
        rowKey="chainId"
        loading={loading}
        scroll={{ x: 1200 }}
        pagination={{
          current: filters.page,
          pageSize: filters.size,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
        }}
        onChange={(pagination) => {
          const newPage = pagination.current || 1;
          const newSize = pagination.pageSize || 20;
          onPageChange(newPage, newSize);
        }}
        onRow={(record) => ({
          onClick: () => onRowClick(record.chainId),
          style: { cursor: 'pointer' },
        })}
        size="middle"
      />
    </div>
  );
};

export default ChainTable;
